/**
 * Lightweight client-side activity tracker.
 *
 * Publishes page views and key user actions to the analytics service.
 * All calls are fire-and-forget — failures are silently swallowed so the
 * UI is never blocked or degraded by analytics issues.
 */

const ACTIVITY_API =
  (import.meta.env.VITE_ANALYTICS_API_BASE_URL || '/api/analytics') +
  '/api/v1/user-activities';

/** Extract userId (sub claim) from a JWT without a library. */
function extractUserIdFromToken(token: string): string | null {
  try {
    const base64Url = token.split('.')[1] || '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload.sub ?? payload.userId ?? payload.user_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Track a single user activity event.
 * Fire-and-forget — never throws and never blocks the caller.
 */
export async function trackActivity(event: {
  eventType: string;
  eventName: string;
  pagePath?: string;
  featureName?: string;
  featureCategory?: string;
  properties?: Record<string, unknown>;
}): Promise<void> {
  try {
    const token = localStorage.getItem('five_ai_access_token');
    if (!token) return;

    const userId = extractUserIdFromToken(token);
    if (!userId) return;

    // Generate a simple session ID scoped to this browser tab
    const sessionId = getOrCreateSessionId();

    await fetch(ACTIVITY_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId,
        sessionId,
        eventType: event.eventType,
        eventName: event.eventName,
        source: 'five-web-portal',
        pagePath: event.pagePath ?? window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        platform: 'Web',
        featureName: event.featureName,
        featureCategory: event.featureCategory,
        properties: event.properties,
      }),
    });
  } catch {
    // Silent fail — never block UI
  }
}

/**
 * Track a page view. Called automatically on route changes.
 */
export function trackPageView(pagePath: string, pageTitle: string): void {
  trackActivity({
    eventType: 'PAGE_VIEW',
    eventName: 'ITEM_VIEWED',
    pagePath,
    properties: { pageTitle },
  });
}

/**
 * Track an AI coach conversation (once per session to avoid flooding).
 */
let _coachTracked = false;
export function trackCoachMessage(): void {
  if (_coachTracked) return;
  _coachTracked = true;
  trackActivity({
    eventType: 'AI_QUERY_SUBMITTED',
    eventName: 'AI_COACH_MESSAGE',
    featureName: 'AI Coach',
    featureCategory: 'coaching',
    pagePath: '/coach',
  });
}

/**
 * Track a user login event.
 */
export function trackLogin(): void {
  trackActivity({
    eventType: 'USER_LOGIN',
    eventName: 'USER_LOGGED_IN',
    featureName: 'Authentication',
    featureCategory: 'auth',
  });
}

/**
 * Track a feature accessed event (lesson started, goal created, etc.).
 */
export function trackFeatureAccess(featureName: string, featureCategory?: string): void {
  trackActivity({
    eventType: 'FEATURE_ACCESSED',
    eventName: 'FEATURE_ACCESSED',
    featureName,
    featureCategory,
  });
}

// ---------------------------------------------------------------------------
// Session ID (tab-scoped, survives SPA navigation but not tab close)
// ---------------------------------------------------------------------------
let _sessionId: string | null = null;

function getOrCreateSessionId(): string {
  if (_sessionId) return _sessionId;
  try {
    const existing = sessionStorage.getItem('five_activity_session_id');
    if (existing) {
      _sessionId = existing;
      return existing;
    }
    const id = 'web-' + crypto.randomUUID();
    sessionStorage.setItem('five_activity_session_id', id);
    _sessionId = id;
    return id;
  } catch {
    _sessionId = 'web-' + Math.random().toString(36).slice(2);
    return _sessionId;
  }
}
